// BookingEditDialog.tsx
// Dialog zum Bearbeiten einer bestehenden Buchung

import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  UserGroupIcon,
  CurrencyEuroIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { adminService } from '../../services/adminService';

interface Booking {
  id: string;
  bookingNumber: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  tripDate: string;
  tripTime: string;
  tripType: string;
  participantCount: number;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  paymentStatus: 'pending' | 'partial' | 'paid' | 'refunded';
  totalAmount: number;
  paidAmount: number;
  notes: string[];
  createdAt: string;
  updatedAt: string;
  extras?: string[];
  specialRequests?: string;
  mealSelections?: MealSelection[];
}

interface MealSelection {
  participantIndex: number;
  mealType: string;
  isIncluded: boolean;
  price: number;
}

interface Extra {
  id: string;
  name: string;
  price: number;
  pricePerPerson: boolean;
}

interface BookingEditDialogProps {
  booking: Booking;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedBooking: Booking) => void;
}

// Available meal options
const MEAL_OPTIONS = [
  { value: 'standard', label: 'Standard-Menü', price: 0, included: true },
  { value: 'vegetarian', label: 'Vegetarisches Menü', price: 5, included: false },
  { value: 'vegan', label: 'Veganes Menü', price: 5, included: false },
  { value: 'kids', label: 'Kinder-Menü', price: 8, included: false },
  { value: 'premium', label: 'Premium-Menü', price: 12, included: false },
];

// Available extras
const AVAILABLE_EXTRAS: Extra[] = [
  { id: 'beer_tasting', name: 'Bierverkostung', price: 10, pricePerPerson: true },
  { id: 'grill_package', name: 'Grillpaket Premium', price: 7, pricePerPerson: true },
  { id: 'dj_service', name: 'DJ-Service', price: 300, pricePerPerson: false },
  { id: 'wedding_deco', name: 'Hochzeitsdekoration', price: 200, pricePerPerson: false },
  { id: 'photographer', name: 'Fotograf (4h)', price: 400, pricePerPerson: false },
  { id: 'cocktail_bar', name: 'Cocktail-Bar', price: 250, pricePerPerson: false },
];

const BookingEditDialog: React.FC<BookingEditDialogProps> = ({
  booking,
  isOpen,
  onClose,
  onSave
}) => {
  // State
  const [participantCount, setParticipantCount] = useState(booking.participantCount);
  const [selectedExtras, setSelectedExtras] = useState<string[]>(booking.extras || []);
  const [mealSelections, setMealSelections] = useState<MealSelection[]>([]);
  const [availableCapacity, setAvailableCapacity] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  // Initialize meal selections
  useEffect(() => {
    if (booking.mealSelections) {
      setMealSelections(booking.mealSelections);
    } else {
      // Initialize with default standard meals
      const initialMeals: MealSelection[] = [];
      for (let i = 0; i < booking.participantCount; i++) {
        initialMeals.push({
          participantIndex: i,
          mealType: 'standard',
          isIncluded: true,
          price: 0
        });
      }
      setMealSelections(initialMeals);
    }
  }, [booking]);

  // Check availability when participant count changes
  useEffect(() => {
    checkAvailability();
  }, [participantCount]);

  const checkAvailability = async () => {
    setLoading(true);
    setError(null);
    setWarning(null);
    
    try {
      // TODO: Replace with real API call
      // const response = await adminService.checkSlotAvailability(booking.slotId);
      
      // Mock response
      const mockCapacity = 140;
      const mockCurrentBookings = 100;
      const available = mockCapacity - mockCurrentBookings;
      
      setAvailableCapacity(available);
      
      if (participantCount > available) {
        setWarning(`Achtung: Nur noch ${available} Plätze verfügbar. Buchung wird überbucht!`);
      }
    } catch (err) {
      console.error('Error checking availability:', err);
      setError('Verfügbarkeit konnte nicht geprüft werden');
    } finally {
      setLoading(false);
    }
  };

  // Handle participant count change
  const handleParticipantCountChange = (newCount: number) => {
    if (newCount < 1) return;
    if (newCount > 200) {
      setError('Maximale Teilnehmerzahl ist 200');
      return;
    }
    
    setParticipantCount(newCount);
    
    // Adjust meal selections
    const currentMeals = [...mealSelections];
    if (newCount > currentMeals.length) {
      // Add new participants with default meal
      for (let i = currentMeals.length; i < newCount; i++) {
        currentMeals.push({
          participantIndex: i,
          mealType: 'standard',
          isIncluded: true,
          price: 0
        });
      }
    } else if (newCount < currentMeals.length) {
      // Remove excess participants
      currentMeals.splice(newCount);
    }
    setMealSelections(currentMeals);
  };

  // Handle meal selection change
  const handleMealChange = (participantIndex: number, mealType: string) => {
    const updatedMeals = [...mealSelections];
    const meal = MEAL_OPTIONS.find(m => m.value === mealType);
    
    if (meal) {
      updatedMeals[participantIndex] = {
        participantIndex,
        mealType,
        isIncluded: meal.included,
        price: meal.price
      };
      setMealSelections(updatedMeals);
    }
  };

  // Handle extra toggle
  const handleExtraToggle = (extraId: string) => {
    setSelectedExtras(prev => {
      if (prev.includes(extraId)) {
        return prev.filter(id => id !== extraId);
      } else {
        return [...prev, extraId];
      }
    });
  };

  // Calculate total price
  const calculateTotalPrice = (): number => {
    let total = 0;
    
    // Base price (from booking variant)
    const basePrice = 63.50; // TODO: Get from booking.tripType
    total += basePrice * participantCount;
    
    // Additional meals
    const mealsCost = mealSelections
      .filter(m => !m.isIncluded)
      .reduce((sum, m) => sum + m.price, 0);
    total += mealsCost;
    
    // Extras
    selectedExtras.forEach(extraId => {
      const extra = AVAILABLE_EXTRAS.find(e => e.id === extraId);
      if (extra) {
        if (extra.pricePerPerson) {
          total += extra.price * participantCount;
        } else {
          total += extra.price;
        }
      }
    });
    
    return total;
  };

  const totalPrice = calculateTotalPrice();
  const priceDifference = totalPrice - booking.totalAmount;

  // Save changes - Backend API Integration
  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Prepare update request gemäß Backend BookingUpdateDto
      const updateRequest = {
        numberOfGuests: participantCount,
        mealSelections: mealSelections.map(meal => ({
          mealId: meal.mealType,
          quantity: 1
        })),
        selectedExtraIds: selectedExtras.map(id => {
          // Convert extra string IDs to numbers
          const extra = AVAILABLE_EXTRAS.find(e => e.id === id);
          return extra ? parseInt(id.replace(/[^0-9]/g, '')) || 0 : 0;
        }).filter(id => id > 0)
      };

      // Call Backend PUT /api/admin/bookings/{id}
      const response = await adminService.updateAdminBooking(parseInt(booking.id), updateRequest);

      // Update local booking object
      const updatedBooking: Booking = {
        ...booking,
        participantCount,
        totalAmount: response.totalPrice || totalPrice,
        extras: selectedExtras,
        mealSelections
      };

      onSave(updatedBooking);
      onClose();

      alert('Buchung erfolgreich aktualisiert!');
    } catch (err: any) {
      console.error('Error updating booking:', err);
      setError(err.response?.data?.message || 'Fehler beim Speichern der Änderungen');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content booking-edit-dialog" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h3>Buchung bearbeiten - {booking.bookingNumber}</h3>
          <button onClick={onClose} className="btn-close" disabled={saving}>
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Error Display */}
          {error && (
            <div className="alert alert-error">
              <ExclamationTriangleIcon className="w-5 h-5" />
              {error}
            </div>
          )}
          
          {/* Warning Display */}
          {warning && (
            <div className="alert alert-warning">
              <ExclamationTriangleIcon className="w-5 h-5" />
              {warning}
            </div>
          )}

          {/* Booking Info Summary */}
          <div className="booking-summary">
            <div className="summary-item">
              <strong>Kunde:</strong> {booking.customerName}
            </div>
            <div className="summary-item">
              <strong>Termin:</strong> {new Date(booking.tripDate).toLocaleDateString('de-DE')} um {booking.tripTime} Uhr
            </div>
            <div className="summary-item">
              <strong>Tour:</strong> {booking.tripType}
            </div>
          </div>

          {/* Participant Count */}
          <div className="form-section">
            <label className="form-label">
              <UserGroupIcon className="w-5 h-5" />
              Teilnehmerzahl
            </label>
            <div className="participant-control">
              <button 
                onClick={() => handleParticipantCountChange(participantCount - 1)}
                disabled={participantCount <= 1 || saving}
                className="btn-control"
              >
                -
              </button>
              <input
                type="number"
                value={participantCount}
                onChange={(e) => handleParticipantCountChange(parseInt(e.target.value) || 0)}
                className="participant-input"
                min="1"
                max="200"
                disabled={saving}
              />
              <button 
                onClick={() => handleParticipantCountChange(participantCount + 1)}
                disabled={saving}
                className="btn-control"
              >
                +
              </button>
            </div>
            {availableCapacity !== null && (
              <div className="capacity-info">
                <span className={availableCapacity > 20 ? 'text-success' : 'text-warning'}>
                  {availableCapacity} Plätze noch verfügbar
                </span>
              </div>
            )}
            {participantCount !== booking.participantCount && (
              <div className="change-info">
                <span className={participantCount > booking.participantCount ? 'text-success' : 'text-warning'}>
                  {participantCount > booking.participantCount ? '+' : ''}{participantCount - booking.participantCount} Personen
                </span>
              </div>
            )}
          </div>

          {/* Meal Selections */}
          <div className="form-section">
            <label className="form-label">Essensauswahl</label>
            <div className="meal-selections-grid">
              {mealSelections.map((meal, index) => (
                <div key={index} className="meal-selection-item">
                  <span className="participant-label">Person {index + 1}</span>
                  <select
                    value={meal.mealType}
                    onChange={(e) => handleMealChange(index, e.target.value)}
                    className="meal-select"
                    disabled={saving}
                  >
                    {MEAL_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label} {!option.included && `(+€${option.price})`}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Extras */}
          <div className="form-section">
            <label className="form-label">Zusatzleistungen</label>
            <div className="extras-grid">
              {AVAILABLE_EXTRAS.map(extra => (
                <div key={extra.id} className="extra-item">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedExtras.includes(extra.id)}
                      onChange={() => handleExtraToggle(extra.id)}
                      disabled={saving}
                    />
                    <span className="extra-name">{extra.name}</span>
                    <span className="extra-price">
                      €{extra.price}{extra.pricePerPerson && '/Person'}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Price Summary */}
          <div className="price-summary">
            <div className="price-row">
              <span>Bisheriger Preis:</span>
              <span>€{booking.totalAmount.toFixed(2)}</span>
            </div>
            <div className="price-row">
              <span>Neuer Preis:</span>
              <span className="new-price">€{totalPrice.toFixed(2)}</span>
            </div>
            <div className={`price-row difference ${priceDifference >= 0 ? 'positive' : 'negative'}`}>
              <span>Differenz:</span>
              <span>
                {priceDifference >= 0 ? '+' : ''}€{priceDifference.toFixed(2)}
              </span>
            </div>
            {booking.paidAmount > 0 && (
              <div className="price-row">
                <span>Bereits bezahlt:</span>
                <span>€{booking.paidAmount.toFixed(2)}</span>
              </div>
            )}
            {priceDifference > 0 && booking.paidAmount > 0 && (
              <div className="price-row highlight">
                <span>Nachzahlung erforderlich:</span>
                <span>€{priceDifference.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button 
            onClick={onClose} 
            className="btn-secondary"
            disabled={saving}
          >
            Abbrechen
          </button>
          <button 
            onClick={handleSave}
            className="btn-primary"
            disabled={saving || loading}
          >
            {saving ? (
              <>
                <div className="spinner-sm"></div>
                Speichern...
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5" />
                Änderungen speichern
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingEditDialog;
